import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

interface VevoDistributionPageProps extends SharedMarketingProps {
  hero: { eyebrow: string; title: string; body: string }
  vevo_logo_url: string
  steps: { step: string; title: string; body: string }[]
  pricing: {
    channel: { label: string; price: string; note: string }
    upload: { label: string; price: string; note: string }
    royalty_note: string
  }
  comparison: {
    wavbee: { name: string; channel: string; per_video: string; recurring: string }
    competitor: { name: string; channel: string; per_video: string; recurring: string }
  }
  benefits: { title: string; body: string }[]
  cta: {
    title: string
    body: string
    actions: { label: string; href: string }[]
  }
}

export default function VevoDistribution({
  hero,
  vevo_logo_url,
  steps,
  pricing,
  comparison,
  benefits,
  cta,
}: VevoDistributionPageProps) {
  return (
    <>
      <AtmosphericHero minHeight="56vh">
        <div className="mx-auto max-w-3xl text-center space-y-10">
          <ScrollReveal>
            <div className="relative mx-auto w-fit">
              <div className="absolute inset-0 scale-150 rounded-full bg-[#ff6a1a]/[0.06] blur-3xl" />
              <img
                src={vevo_logo_url}
                alt="VEVO"
                className="relative h-12 object-contain invert lg:h-16"
              />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>{hero.title}</h1>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <p className="hero-body mx-auto">{hero.body}</p>
          </ScrollReveal>
        </div>
      </AtmosphericHero>

      {/* How it works */}
      <section className="container mx-auto px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <h2
              className="marketing-section-title text-center text-white"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
            >
              How it works
            </h2>
          </ScrollReveal>
          <div className="relative mt-12">
            {/* Connecting line — desktop only */}
            <div className="absolute left-1/2 top-0 hidden h-full w-px bg-gradient-to-b from-[#ff6a1a]/40 via-[#ff6a1a]/20 to-transparent lg:block" />

            <div className="space-y-5 lg:space-y-10">
              {steps.map((step, index) => (
                <ScrollReveal key={step.step} delay={index * 0.1}>
                  {/* Desktop: alternating timeline */}
                  <div className={`hidden lg:flex items-start gap-12 ${index % 2 === 1 ? 'flex-row-reverse' : ''}`}>
                    <div className="flex-1" />
                    <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#ff6a1a]/30 bg-[#0a0a0a]">
                      <span className="text-sm font-bold text-[#ff6a1a]">{step.step}</span>
                    </div>
                    <div className="flex-1 rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-white/[0.16] hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
                      <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'var(--wavbee-font-display)' }}>
                        {step.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-white/60">{step.body}</p>
                    </div>
                  </div>

                  {/* Mobile: simple stacked cards with step accent */}
                  <div className="flex items-start gap-4 lg:hidden">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#ff6a1a]/30 bg-[#ff6a1a]/10">
                      <span className="text-xs font-bold text-[#ff6a1a]">{step.step}</span>
                    </div>
                    <div className="flex-1 rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-5">
                      <h3 className="text-base font-semibold text-white">{step.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-white/60">{step.body}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="container mx-auto px-6 py-20">
        <ScrollReveal>
          <div className="mx-auto max-w-3xl">
            <h2
              className="marketing-section-title text-center text-white"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
            >
              Simple pricing
            </h2>
            <div className="mt-10 grid gap-5 sm:grid-cols-2">
              {/* Channel card */}
              <div className="relative overflow-hidden rounded-[1.75rem] border border-[#ff6a1a]/20 bg-gradient-to-br from-[#ff6a1a]/10 to-[#ff6a1a]/[0.02] p-8 text-center transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(255,106,26,0.08)]">
                <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#ff6a1a]/[0.08] blur-3xl" />
                <p className="text-sm font-medium uppercase tracking-wider text-[#ff6a1a]/60">
                  {pricing.channel.label}
                </p>
                <p
                  className="mt-4 text-5xl font-bold text-[#ff6a1a]"
                  style={{ fontFamily: 'var(--wavbee-font-display)' }}
                >
                  {pricing.channel.price}
                </p>
                <p className="mt-3 text-sm text-white/56">{pricing.channel.note}</p>
              </div>

              {/* Per video card */}
              <div className="rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-8 text-center transition-all duration-200 hover:-translate-y-1 hover:border-white/[0.16] hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
                <p className="text-sm font-medium uppercase tracking-wider text-white/48">
                  {pricing.upload.label}
                </p>
                <p
                  className="mt-4 text-5xl font-bold text-white"
                  style={{ fontFamily: 'var(--wavbee-font-display)' }}
                >
                  {pricing.upload.price}
                </p>
                <p className="mt-3 text-sm text-white/56">{pricing.upload.note}</p>
              </div>
            </div>
            <p className="mt-6 text-center text-sm text-white/50">{pricing.royalty_note}</p>
          </div>
        </ScrollReveal>
      </section>

      {/* Comparison */}
      <section className="container mx-auto px-6 py-20">
        <ScrollReveal>
          <div className="mx-auto max-w-3xl">
            <h2
              className="marketing-section-title text-center text-white"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
            >
              wavbee vs the competition
            </h2>
            <div className="mt-10 overflow-hidden rounded-[1.75rem] border border-white/[0.08]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.08] bg-white/[0.04]">
                    <th className="px-6 py-5 text-left font-medium text-white/40" />
                    <th className="px-6 py-5 text-left">
                      <span className="font-bold text-[#ff6a1a]">{comparison.wavbee.name}</span>
                    </th>
                    <th className="px-6 py-5 text-left font-medium text-white/40">{comparison.competitor.name}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'VEVO Channel', w: comparison.wavbee.channel, c: comparison.competitor.channel },
                    { label: 'Per Video', w: comparison.wavbee.per_video, c: comparison.competitor.per_video },
                    { label: 'Recurring Fee', w: comparison.wavbee.recurring, c: comparison.competitor.recurring },
                  ].map((row, i, arr) => (
                    <tr key={row.label} className={i < arr.length - 1 ? 'border-b border-white/[0.06]' : ''}>
                      <td className="px-6 py-5 text-white/60">{row.label}</td>
                      <td className="px-6 py-5">
                        <span className="rounded-full bg-[#ff6a1a]/10 px-3 py-1 text-sm font-medium text-[#ff6a1a]">
                          {row.w}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-white/36">{row.c}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Benefits */}
      <section className="container mx-auto px-6 py-20">
        <ScrollReveal>
          <h2
            className="marketing-section-title mx-auto max-w-4xl text-center text-white"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
          >
            Why artists choose wavbee for VEVO
          </h2>
        </ScrollReveal>
        <div className="mx-auto mt-10 grid max-w-4xl gap-5 sm:grid-cols-2">
          {benefits.map((benefit, index) => (
            <ScrollReveal key={benefit.title} delay={index * 0.08}>
              <div className="group h-full rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-7 transition-all duration-200 hover:-translate-y-1 hover:border-[#ff6a1a]/20 hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
                <div className="mb-4 h-1 w-8 rounded-full bg-[#ff6a1a]/30 transition-all duration-200 group-hover:w-12 group-hover:bg-[#ff6a1a]/60" />
                <h3
                  className="text-base font-semibold text-white"
                  style={{ fontFamily: 'var(--wavbee-font-display)' }}
                >
                  {benefit.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/50 transition-colors duration-200 group-hover:text-white/70">
                  {benefit.body}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-6 py-24">
        <ScrollReveal>
          <div className="marketing-final-cta mx-auto max-w-2xl rounded-[1.75rem] border border-white/[0.08] p-10 text-center">
            <h2
              className="text-2xl font-bold text-white"
              style={{ fontFamily: 'var(--wavbee-font-display)' }}
            >
              {cta.title}
            </h2>
            <p className="mt-3 text-white/60">{cta.body}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {cta.actions.map((action, index) => (
                <a
                  key={action.label}
                  href={action.href}
                  className={index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'}
                >
                  {action.label}
                </a>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </section>
    </>
  )
}
